<template>
  <div>
    <Breadcrumb breadcrumb="Blogs" />

    <div v-if="isLoading">
      <div class="flex justify-center items-center h-screen">
        <img :src="loader" alt="Loading..." class="w-8 h-8" />
      </div>
    </div>
    <div v-else class="flex flex-col mt-8">
      <div class="flex justify-between items-center mb-6 mt-6">
        <h2 class="text-xl font-semibold leading-tight text-gray-700">Blogs</h2>
        <button
          v-if="userRoleId === '1'"
          class="px-4 py-2 font-medium tracking-wide text-white capitalize transition-colors duration-200 transform bg-indigo-600 rounded-md hover:bg-indigo-500 focus:outline-none focus:bg-indigo-500"
          @click="newBlogModalOpen = true"
        >
          Create new blog
        </button>
      </div>
      <div class="py-2 -my-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div class="inline-block min-w-full overflow-hidden align-middle border-b border-gray-200 shadow sm:rounded-lg">
          <table class="min-w-full">
            <thead>
              <tr>
                <th
                  v-for="(heading, index) in tableHeadings"
                  :key="index"
                  class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50 whitespace-nowrap"
                >
                  {{ heading }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white">
              <tr v-for="(blog, index) in blogs" :key="index">
                <td class="px-6 py-4 border-b border-gray-200 whitespace-nowrap text-sm">
                  {{ blog.title }}
                </td>
                <td class="px-6 py-4 border-b border-gray-200 whitespace-nowrap text-sm">
                  {{ blog.category.name }}
                </td>
                <td class="px-6 py-4 border-b border-gray-200 whitespace-nowrap text-sm">
                  {{ blog.type.name }}
                </td>
                <td class="px-6 py-4 border-b border-gray-200 whitespace-nowrap text-sm">
                  <span
                    :class="
                      blog.status.name === 'published'
                        ? 'text-blue-500 bg-blue-100'
                        : blog.status.name === 'approved'
                        ? 'text-green-500 bg-green-100'
                        : blog.status.name === 'refused'
                        ? 'text-red-500 bg-red-100'
                        : blog.status.name === 'unpublished'
                        ? 'text-orange-500 bg-red-100'
                        : 'text-amber-500 bg-amber-100'
                    "
                    class="inline-flex px-2 py-1 text-xs font-semibold leading-5 rounded-full"
                    >{{ blog.status.name }}</span
                  >
                </td>
                <td class="px-6 py-4 border-b border-gray-200 whitespace-nowrap text-sm">
                  {{ formatDate(blog.created_at) }}
                </td>
                <td class="px-6 py-4 border-b border-gray-200 whitespace-nowrap text-sm">
                  {{ blog.published_at ? formatDate(blog.published_at) : "N/A" }}
                </td>
                <td class="px-6 py-4 border-b border-gray-200 whitespace-nowrap text-sm">
                  {{ Math.floor(blog.views) }}
                </td>
                <td class="px-6 py-4 border-b border-gray-200 whitespace-nowrap text-sm">
                  <div class="flex gap-6">
                    <span class="cursor-pointer" @click="openViewBlogModal(blog.id)">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5 text-gray-700">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                        />
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    </span>
                    <span class="cursor-pointer" @click="openEditBlogModal(blog.id)">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5 text-gray-700">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                        />
                      </svg>
                    </span>
                    <span class="cursor-pointer" @click="openDeleteModal(blog.id, blog.status_id)">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5 text-red-700">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                        />
                      </svg>
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="blogs.length === 0" class="w-full text-center py-4 text-sm normal-case text-gray-500">No blogs to display</div>
        </div>
      </div>
    </div>

    <!-- new blog modal -->
    <div :class="`modal ${!newBlogModalOpen && 'opacity-0 pointer-events-none'} z-50 fixed w-full h-full top-0 left-0 flex items-center justify-center`">
      <div @click="newBlogModalOpen = false" class="absolute w-full h-full bg-gray-900 opacity-50 modal-overlay"></div>

      <div class="z-50 w-11/12 mx-auto overflow-y-auto bg-white rounded shadow-lg modal-container md:max-w-6xl h-4/5">
        <div class="absolute top-0 right-0 z-50 flex flex-col items-center mt-4 mr-4 text-sm text-white cursor-pointer modal-close">
          <div class="z-50 cursor-pointer modal-close" @click="newBlogModalOpen = false">
            <svg class="text-white fill-current" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
              <path d="M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z" />
            </svg>
          </div>
        </div>

        <NewBlogModal :categories="categories" :types="types" />
      </div>
    </div>

    <!-- view blog modal -->
    <div :class="`modal ${!viewBlogModalOpen && 'opacity-0 pointer-events-none'} z-50 fixed w-full h-full top-0 left-0 flex items-center justify-center`">
      <div @click="viewBlogModalOpen = false" class="absolute w-full h-full bg-gray-900 opacity-50 modal-overlay"></div>

      <div class="z-50 w-11/12 mx-auto overflow-y-auto bg-white rounded shadow-lg modal-container md:max-w-6xl h-4/5">
        <div class="absolute top-0 right-0 z-50 flex flex-col items-center mt-4 mr-4 text-sm text-white cursor-pointer modal-close">
          <div class="z-50 cursor-pointer modal-close" @click="viewBlogModalOpen = false">
            <svg class="text-white fill-current" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
              <path d="M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z" />
            </svg>
          </div>
        </div>

        <ViewBlogModal :id="selectedBlogId" :statuses="statuses" :categories="categories" :types="types" />
      </div>
    </div>

    <!-- edit blog modal -->
    <div :class="`modal ${!editBlogModalOpen && 'opacity-0 pointer-events-none'} z-50 fixed w-full h-full top-0 left-0 flex items-center justify-center`">
      <div @click="editBlogModalOpen = false" class="absolute w-full h-full bg-gray-900 opacity-50 modal-overlay"></div>

      <div class="z-50 w-11/12 mx-auto overflow-y-auto bg-white rounded shadow-lg modal-container md:max-w-6xl h-4/5">
        <div class="absolute top-0 right-0 z-50 flex flex-col items-center mt-4 mr-4 text-sm text-white cursor-pointer modal-close">
          <div class="z-50 cursor-pointer modal-close" @click="editBlogModalOpen = false">
            <svg class="text-white fill-current" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
              <path d="M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z" />
            </svg>
          </div>
        </div>

        <EditBlogModal :id="selectedBlogId" :categories="categories" :types="types" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from "vue";
import axios from "axios";
import Breadcrumb from "../partials/AppBreadcrumb.vue";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import NewBlogModal from "@/components/blogs/NewBlogModal.vue";
import ViewBlogModal from "@/components/blogs/ViewBlogModal.vue";
import EditBlogModal from "@/components/blogs/EditBlogModal.vue";
import Swal from "sweetalert2";
const baseUrl = process.env.VUE_APP_BASE_URL;
import loader from "../assets/loader.svg";

const blogs = ref([]);
const loading = ref(true);
const tableHeadings = ["title", "category", "type", "status", "created at", "published at", "views", "action"];
const newBlogModalOpen = ref(false);
const viewBlogModalOpen = ref(false);
const editBlogModalOpen = ref(false);
const selectedBlogId = ref();
const categories = ref([]);
const statuses = ref([]);
const types = ref([]);
const userRoleId = localStorage.getItem("user_role_id");
const isLoading = ref(true);

const formatDate = (date: string) => {
  if (date === null || date === undefined || date === "null") {
    return "N/A";
  } else {
    return new Date(date).toLocaleDateString();
  }
};

const fetchBlogs = async () => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.get(`${baseUrl}/blog/get`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    blogs.value = response.data.data;
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const fetchCategories = () => {
  const token = localStorage.getItem("token");

  axios
    .get(`${baseUrl}/blog-category/get`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((response) => {
      categories.value = response.data.data;
    })
    .catch((error) => {
      console.error(error);
    });
};

const fetchStatuses = () => {
  const token = localStorage.getItem("token");

  axios
    .get(`${baseUrl}/blog-status/get`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((response) => {
      statuses.value = response.data.data;
    })
    .catch((error) => {
      console.error(error);
    });
};

const fetchTypes = () => {
  const token = localStorage.getItem("token");

  axios
    .get(`${baseUrl}/blog-type/get`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((response) => {
      types.value = response.data.data;
    })
    .catch((error) => {
      console.error(error);
    });
};

const initialProcess = async () => {
  await fetchBlogs();
  fetchCategories();
  fetchStatuses();
  fetchTypes();
};

onMounted(() => {
  initialProcess();
});

const openDeleteModal = (id, status_id) => {
  if (status_id === 1) {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteBlog(id);
      }
    });
  } else {
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: "You cannot delete an approved/ published blog!",
    });
  }
};

const deleteBlog = (id) => {
  const token = localStorage.getItem("token");
  axios
    .delete(`${baseUrl}/blog/delete/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((response) => {
      Swal.fire("Deleted!", "Your file has been deleted.", "success");
      fetchBlogs();
    })
    .catch((error) => {
      console.error(error);
    });
};

const openViewBlogModal = (id) => {
  viewBlogModalOpen.value = true;
  selectedBlogId.value = id;
};

const openEditBlogModal = (id) => {
  editBlogModalOpen.value = true;
  selectedBlogId.value = id;
};

watch(newBlogModalOpen, (value) => {
  if (!value) {
    fetchBlogs();
  }
});

watch(viewBlogModalOpen, (value) => {
  if (!value) {
    fetchBlogs();
  }
});

watch(editBlogModalOpen, (value) => {
  if (!value) {
    fetchBlogs();
  }
});

watch([blogs, categories, statuses, types], () => {
  if (blogs.value.length > 0 && categories.value.length > 0 && statuses.value.length > 0 && types.value.length > 0) {
    isLoading.value = false;
  }
});
</script>

<style>
.modal {
  transition: opacity 0.25s ease;
}
</style>
